import { useState } from "react";
import { useServiceAreas } from "@/hooks/useServiceAreas";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Plus, Loader2, MapPin, Heart, Star } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { isValidZip, filterZipInput, getEdgeFunctionErrorMessage } from "@/lib/validation";
import { HC_SPECIALTY_LABELS, GH_SPECIALTY_LABELS } from "@/lib/specialties";

export default function LeadFilters() {
  const { serviceAreas, loading: areasLoading, addServiceArea, updateServiceArea, deleteServiceArea } = useServiceAreas();
  const { profile, updateProfile } = useProfile();
  const seg = (profile as any)?.segment || "homecare";
  const CARE_SPECIALTIES = seg === "grouphome" ? GH_SPECIALTY_LABELS : HC_SPECIALTY_LABELS;
  const [newZip, setNewZip] = useState("");
  const [addingZip, setAddingZip] = useState(false);
  const [rescoring, setRescoring] = useState(false);

  const specialties: { name: string; enabled: boolean; priority: boolean }[] = 
    (profile?.specialty_preferences as any[]) || [];

  function isSpecEnabled(name: string) {
    return specialties.some(s => s.name === name && s.enabled);
  }

  function isSpecPriority(name: string) {
    return specialties.some(s => s.name === name && s.priority);
  }

  async function toggleSpecialty(name: string) {
    const existing = specialties.find(s => s.name === name);
    let updated: any[];
    if (existing) {
      updated = specialties.map(s => s.name === name ? { ...s, enabled: !s.enabled } : s);
    } else {
      updated = [...specialties, { name, enabled: true, priority: false }];
    }
    await updateProfile({ specialty_preferences: updated } as any);
  }

  async function togglePriority(name: string) {
    const updated = specialties.map(s => s.name === name ? { ...s, priority: !s.priority } : s);
    await updateProfile({ specialty_preferences: updated } as any);
  }

  async function handleAddZip() {
    if (!isValidZip(newZip)) {
      toast.error("Enter a valid 5-digit ZIP code");
      return;
    }
    setAddingZip(true);
    try {
      await addServiceArea(newZip);
      setNewZip("");
      toast.success(`Added ZIP ${newZip}`);
    } catch (e: any) {
      if (e.message?.includes("duplicate")) toast.error("ZIP already added");
      else toast.error(e.message || "Failed to add ZIP");
    } finally {
      setAddingZip(false);
    }
  }

  async function handleRescore() {
    setRescoring(true);
    try {
      const { data, error } = await supabase.functions.invoke("score-lead", {
        body: { bulk: true },
      });
      if (error) throw error;
      toast.success(`Re-scored ${data.updated} leads with new filters`);
    } catch (e: any) {
      toast.error(await getEdgeFunctionErrorMessage(e, "Re-scoring failed"));
    } finally {
      setRescoring(false);
    }
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">{seg === "grouphome" ? "Inquiry Filters" : "Lead Filters"}</h2>
          <p className="text-sm text-muted-foreground">{seg === "grouphome" ? "Configure localities and populations to power predictive inquiry scoring." : "Configure service areas and care specialties to power predictive lead scoring."}</p>
        </div>

        {/* Service Areas */}
        <div className="rounded-xl border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">Service Areas</h3>
          </div>
          <p className="text-sm text-muted-foreground">Add ZIP codes your agency serves. Mark high-value areas with the star.</p>

          <div className="flex gap-2">
            <Input
              placeholder="Enter ZIP code"
              type="text"
              inputMode="numeric"
              value={newZip}
              onChange={e => setNewZip(filterZipInput(e.target.value))}
              maxLength={5}
              className="w-40"
              onKeyDown={e => e.key === "Enter" && handleAddZip()}
            />
            <Button onClick={handleAddZip} disabled={addingZip} size="sm" className="gap-1">
              {addingZip ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              Add
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {areasLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : serviceAreas.length === 0 ? (
              <p className="text-sm text-muted-foreground">No service areas yet</p>
            ) : (
              serviceAreas.map(sa => (
                <div key={sa.id} className="flex items-center gap-1 rounded-lg border bg-secondary/50 px-3 py-1.5">
                  <span className="text-sm font-medium">{sa.zip_code}</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => updateServiceArea(sa.id, { is_golden_zone: !sa.is_golden_zone })}
                        className="ml-1"
                      >
                        <Star
                          className={`h-3.5 w-3.5 transition-colors ${
                            sa.is_golden_zone
                              ? "fill-yellow-500 text-yellow-500"
                              : "text-muted-foreground hover:text-yellow-500"
                          }`}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {sa.is_golden_zone ? "High-value area (click to remove)" : "Mark as high-value area"}
                    </TooltipContent>
                  </Tooltip>
                  <button onClick={() => deleteServiceArea(sa.id)} className="ml-1 text-muted-foreground hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Care Specialties */}
        <div className="rounded-xl border bg-card p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">{seg === "grouphome" ? "Population Specialties" : "Care Specialties"}</h3>
          </div>
          <p className="text-sm text-muted-foreground">Select specialties your agency handles. Mark priority specialties for an extra scoring boost.</p>

          <div className="divide-y rounded-lg border">
            {CARE_SPECIALTIES.map(name => (
              <div
                key={name}
                className={`flex flex-col gap-2 p-3 transition-colors sm:flex-row sm:items-center sm:justify-between ${
                  isSpecEnabled(name) ? "bg-primary/5" : "hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">{name}</span>
                  {isSpecEnabled(name) && (
                    <label
                      htmlFor={`priority-${name}`}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"
                    >
                      <Checkbox
                        checked={isSpecPriority(name)}
                        onCheckedChange={() => togglePriority(name)}
                        id={`priority-${name}`}
                      />
                      Priority (+10 pts)
                    </label>
                  )}
                </div>
                <Switch
                  checked={isSpecEnabled(name)}
                  onCheckedChange={() => toggleSpecialty(name)}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Re-score */}
        <Button onClick={handleRescore} disabled={rescoring} className="gap-2">
          {rescoring ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {rescoring ? "Re-scoring all leads…" : "Save & Re-Score All Leads"}
        </Button>
      </div>
    </TooltipProvider>
  );
}
